// @flow

import { graphql } from "gatsby";
import { GatsbyImage, getSrc } from "gatsby-plugin-image";
import React, { useState, useEffect } from "react";
import { t, useLocale, addLocale, gettext } from "ttag";
import {
    PropertyMainInformation,
    PropertyImageCarousel,
    PropertySlider,
    UspTiles,
    Lightbox,
    MediaActionBar,
} from "@2po-c21/components";
import {
    Section,
    PropertyDescription,
    PageContent,
    PropertyDetailsHeader,
    CarouselFallbackImage,
} from "@containers";
import {
    AgencyContactForm,
    PropertyDetailsBreadcrumb,
    PropertyDetailsMeta,
    PropertyDetailsProjectPropertiesDisplay as ProjectPropertiesDisplay,
    PropertyHeader,
    PropertyLocation,
    SimilarPropertiesDisplay,
    PropertyButtonOverlay,
} from "@components";
import { Row, Col, Container } from "reactstrap";
import { useGTMDispatch } from "@elgorditosalsero/react-gtm-hook";
import {
    type ImagesSliderEvent,
    type SliderImageClickEvent,
    type DetailElementClickEvent,
    EVENT_MAP,
    type PageImpressionParams,
} from "../models/events";
import { PushEvent, TrackPageImpression } from "../components/Tracker/Tracker";
import Render from "../components/Contentful";
import { PropertyBanner } from "../components";
import { propertySubTypes, propertyTypes } from "../utils/propertyTypes";

const toFluidImages = (images) => {
    if (!images) {
        return null;
    }

    return images.map((image) => {
        return {
            description: image.description,
            imageSrc: () => (
                <GatsbyImage
                    image={image.gatsbyImageData}
                    alt={image.description}
                    objectFit={"contain"}
                    style={{
                        margin: "auto",
                        maxWidth: "100%",
                    }}
                    loading="eager"
                />
            ),
        };
    });
};

const smallImages = (images) => {
    if (!images) {
        return null;
    }

    return images.map((image) => {
        return {
            description: image.description,
            imageSrc: () => (
                <GatsbyImage
                    image={image.gatsbyImageData}
                    alt={image.description}
                    style={{
                        width: "100%",
                        height: "100%",
                        maxWidth: "132px",
                        maxHeight: "122px",
                    }}
                    loading="eager"
                />
            ),
        };
    });
};

const PropertyDetailsPageTemplate = ({
    data: {
        property,
        agencyFallbackImage,
        carouselFallbackImage: {
            childImageSharp: { fluid },
        },
        c21Translation: { translations },
        contentfulNavigation: { navigationItems, primaryLink },
        footerItems,
        topNavigationItems,
        contentfulWebLandingPage: { pageContent },
        socialLinks,
    },
    pageContext: { translationSlugs, country },
}: *) => {
    const {
        carouselImages,
        address,
        agent,
        agency,
        locale,
        condition,
        metaImage,
        status,
        propertyId,
    } = property;
    const gtmDispatcher = useGTMDispatch();
    addLocale(property.locale, translations);
    useLocale(property.locale);

    const [hasMounted, setHasMounted] = useState(false);
    const [lightboxState, setLightboxState] = useState({
        isOpen: false,
        activeContent: null,
    });

    useEffect(() => {
        const params: PageImpressionParams = {
            agency: {
                id: agency?.api_id,
                name: agency?.name,
                postalcode: agency?.address.postalCode,
                city: agency?.address.city,
                country: agency?.address.country,
            },
            agencypage: "false",
            language: locale,
            pagename: "Property details",
            typetemplate: "property-details",
        };
        TrackPageImpression(gtmDispatcher, EVENT_MAP.PageImpression, params);
    }, []);

    useEffect(() => {
        setHasMounted(true);
    }, []);

    const isNewProperty = () => {
        if (!property.datePosted) return false;
        const oneWeekAgo = new Date();
        oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
        const postedDate = new Date(property.datePosted);
        return postedDate >= oneWeekAgo;
    };

    const badge = (() => {
        if (condition === "NEW" && isNewProperty()) {
            return {
                text: [
                    `${gettext(
                        "api.property.condition.NEW_PROPERTY",
                    )} | ${gettext("api.property.condition.NEW")}`,
                ],
                variant: "new",
            };
        } else if (condition === "NEW") {
            return {
                text: gettext("api.property.condition.NEW"),
                variant: "",
            };
        } else if (status === "OPTION") {
            return {
                text: gettext("api.property.status.OPTION"),
                variant: "info",
            };
        } else if (isNewProperty()) {
            return {
                text: gettext("api.property.condition.NEW_PROPERTY"),
                variant: "new",
            };
        }
        return {};
    })();

    const propertyImageCarouselContent = (activeIndex) => {
        return (
            <PropertyImageCarousel
                images={toFluidImages(carouselImages)}
                largeImage
                reactToArrowKeys={true}
                initialIndex={activeIndex}
                disableReset={true}
                handleOnNext={() =>
                    handleOnNext(property, "property detail overlay")
                }
                handleOnPrevious={() =>
                    handleOnPrevious(property, "property detail overlay")
                }
            />
        );
    };

    const openLightbox = (contentSource) => {
        setLightboxState({
            ...lightboxState,
            isOpen: true,
            activeContent: contentSource(),
        });
    };
    const sendEvent = (name: string, element: string, targetUrl: string) => {
        const gtmEvent: DetailElementClickEvent = {
            name,
            element,
            targeturl: targetUrl,
            type: EVENT_MAP.DetailElementClickEvent,
            propertyid: propertyId,
        };
        PushEvent(gtmEvent, gtmDispatcher);
    };
    const handleClickVisitProperty = () => {
        sendEvent("visit property", "button", AgencyContactForm.anchor);
    };
    const fallbackImage = <CarouselFallbackImage fluid={fluid} />;

    const EMPTY_VALUE = "UNKNOWN";

    const breadCrumbPropertyTitle =
        property.title ||
        (property.subType === "UNKNOWN" || !property.subType
            ? gettext(propertyTypes[property.type])
            : gettext(propertySubTypes[property.subType]));

    const pushImagesSlider = (
        direction: string,
        projectid: string,
        listname: string,
    ) => {
        const gtmEvent: ImagesSliderEvent = {
            direction,
            listname: listname,
            propertyid: projectid,
            propertyview: "detail",
            type: EVENT_MAP.ImagesSliderEvent,
        };
        PushEvent(gtmEvent, gtmDispatcher);
    };

    const handleOnNext = (property: any, listname: string) => {
        pushImagesSlider("right", property.id, listname);
    };
    const handleOnPrevious = (property: any, listname: string) => {
        pushImagesSlider("left", property.id, listname);
    };
    const handleOnClick = (
        imageKeyIndex: number,
        listname: string = "property detail",
    ) => {
        const image =
            getSrc(property.carouselImages[imageKeyIndex]?.gatsbyImageData) ||
            undefined;
        const amountImages = property.carouselImages.length;

        const gtmEvent: SliderImageClickEvent = {
            name: image,
            propertyid: property.id,
            listtotal: amountImages,
            propertyview: "detail",
            listname,
            position: imageKeyIndex + 1,
            type: EVENT_MAP.SliderImageClickEvent,
        };

        PushEvent(gtmEvent, gtmDispatcher);
    };

    const scrollToElement = (elementId) => {
        const element = document.querySelector(elementId);
        if (element) {
            element.scrollIntoView({ behavior: "smooth" });
        }
    };

    return (
        <>
            <script type="application/ld+json">
                {JSON.stringify({
                    "@context": "https://schema.org",
                    "@type": "RealEstateListing",
                    name: property?.title,
                    description: property?.description,
                    url:
                        typeof window !== "undefined"
                            ? window.location.href
                            : "",
                    datePosted: property?.datePosted, // currently not available in the properties API
                    offers: {
                        "@type": "Offer",
                        price: property?.price?.substring(1),
                        priceCurrency: "EUR",
                    },
                    address: {
                        "@type": "PostalAddress",
                        streetAddress: `${property?.address?.street} ${property?.address?.number}`,
                        addressLocality: property?.address?.city,
                        addressRegion: property?.address?.region, // currently not available in the properties API
                        postalCode: property?.address?.postalCode,
                        addressCountry: property?.address?.countryCode,
                    },
                })}
            </script>
            <PageContent
                locale={locale}
                navigationItems={navigationItems}
                topNavigationItems={topNavigationItems?.navigationItems}
                primaryLink={primaryLink}
                translationSlugs={translationSlugs}
                footerItems={footerItems}
                socialLinks={socialLinks}
                country={country}
            >
                {/* these buttons are only shown on mobile and on this page */}
                <PropertyButtonOverlay
                    onClickVisitProperty={() => {
                        scrollToElement(AgencyContactForm.anchor);
                        handleClickVisitProperty();
                    }}
                    phoneNumber={agency?.phoneNumber}
                    contactText={t`property-details.property-description.contact-agency`}
                    visitPropertyBtnText="Demande d'infos"
                />
                <PropertyDetailsMeta
                    property={property}
                    translationSlugs={translationSlugs}
                    locale={locale}
                    metaImage={
                        metaImage[0] ? metaImage[0]?.meta?.src : fluid.src
                    }
                />
                {hasMounted && (
                    <Lightbox
                        isOpen={lightboxState.isOpen}
                        contentSource={() => lightboxState.activeContent}
                        onClickOutside={() =>
                            setLightboxState({
                                ...lightboxState,
                                isOpen: false,
                            })
                        }
                    />
                )}
                <Container fluid className="px-0">
                    <PropertyHeader>
                        <Row>
                            <Col xl={{ size: 7, order: 1 }} className="px-0">
                                {carouselImages.length > 0 ? (
                                    <>
                                        <PropertySlider
                                            mainImage={{
                                                description:
                                                    carouselImages[0]
                                                        .description,
                                                imageSrc: () => (
                                                    <GatsbyImage
                                                        image={
                                                            carouselImages[0]
                                                                .gatsbyImageData
                                                        }
                                                        alt={
                                                            carouselImages[0]
                                                                .description
                                                        }
                                                        style={{
                                                            width: "100%",
                                                            height: "100%",
                                                            maxHeight: "415px",
                                                        }}
                                                        loading="eager"
                                                    />
                                                ),
                                            }}
                                            images={smallImages(carouselImages)}
                                            onClick={(key) => {
                                                handleOnClick(key);
                                                openLightbox(() =>
                                                    propertyImageCarouselContent(
                                                        key,
                                                    ),
                                                );
                                            }}
                                            badge={badge}
                                        />
                                        {property.urlVirtualVisit && (
                                            <MediaActionBar
                                                urlVirtualVisit={
                                                    property.urlVirtualVisit
                                                }
                                                urlVirtualVisitLabel={gettext(
                                                    "property-details.general-information.virtualVisit",
                                                )}
                                                videoUrl={property.videoUrl}
                                            />
                                        )}
                                    </>
                                ) : (
                                    fallbackImage
                                )}
                            </Col>
                            <Col xl={{ size: 5, order: 0 }}>
                                <PropertyMainInformation
                                    title={property.title}
                                    subtitle={`${gettext(
                                        `api.property.listing-type.${property.listingType}`,
                                    )}`}
                                    condition={
                                        condition === EMPTY_VALUE
                                            ? null
                                            : gettext(
                                                  `api.property.condition.${condition}`,
                                              )
                                    }
                                    listingType={`${gettext(
                                        `api.property.listing-type.${property.listingType}`,
                                    )}`}
                                    address={address}
                                    addressHiddenText={gettext(
                                        "common.address.on-demand",
                                    )}
                                    onClickAddress={() => {
                                        scrollToElement("#property-map");
                                    }}
                                    price={property.price}
                                    priceLabel={gettext(
                                        "common.filters.price.label",
                                    )}
                                    urlVirtualVisit={property.urlVirtualVisit}
                                    urlVirtualVisitLabel={gettext(
                                        "property-details.general-information.virtualVisit",
                                    )}
                                    breadcrumb={
                                        <PropertyDetailsBreadcrumb
                                            locale={locale}
                                            propertyTitle={
                                                breadCrumbPropertyTitle
                                            }
                                            variant="gold"
                                        />
                                    }
                                />
                                <PropertyDetailsHeader property={property} />
                            </Col>
                        </Row>
                    </PropertyHeader>
                </Container>
                <Section>
                    <PropertyDescription
                        property={property}
                        title={t`property-details.description.title`}
                        subTitle={t`property-details.description.sub-title`}
                        description={property.description}
                        agent={agent}
                        agency={agency}
                        fallbackImage={agencyFallbackImage.childImageSharp}
                        contactFormAnchor={AgencyContactForm.anchor}
                        propertyId={property.id}
                        locale={locale}
                    />
                </Section>
                {property.project ? (
                    <Section>
                        <ProjectPropertiesDisplay property={property} />
                    </Section>
                ) : null}

                {pageContent &&
                    renderPropertyDetailsBanner(
                        pageContent?.filter((item) => {
                            if (
                                item.__typename ===
                                    "ContentfulSearchPageBanner" &&
                                [
                                    "ALL",
                                    process.env.GATSBY_COUNTRY_CODE?.toUpperCase() ||
                                        "",
                                ].includes(item.country)
                            ) {
                                return item;
                            }
                        }),
                        locale,
                        property.listingType,
                    )}

                {property &&
                    !property?.address?.hidden &&
                    property.location &&
                    !isDefaultLocation(property.location) && (
                        <Container fluid id="property-map">
                            <PropertyLocation property={property} />
                        </Container>
                    )}
                {agency && (
                    <AgencyContactForm
                        agencyApiId={agency.api_id}
                        locale={locale}
                        type={"property"}
                        property={property}
                        propertyAddressHidden={property?.address?.hidden}
                    />
                )}
                {renderUsp()}
                {property && (
                    <Section>
                        <SimilarPropertiesDisplay property={property} />
                    </Section>
                )}
            </PageContent>
        </>
    );
};

const renderUsp = () => (
    <UspTiles
        tiles={[
            { data: t`usp.network.title`, label: t`usp.network.subtitle` },
            { data: t`usp.sales.title`, label: t`usp.sales.subtitle` },
            {
                data: t`usp.relationships.title`,
                label: t`usp.relationships.subtitle`,
            },
        ]}
    />
);

const renderPropertyDetailsBanner = (searchBanner, locale, filter) => {
    if (searchBanner?.length) {
        const filteredBanners = (type) =>
            searchBanner?.filter((item) => item.typeOfSearch === type);
        let toRender = null;

        if (filter == "FOR_RENT")
            toRender = Render(filteredBanners("For rent"), locale);

        if (filter == "FOR_SALE")
            toRender = Render(filteredBanners("For sale"), locale);

        return (
            <PropertyBanner>
                {Render(filteredBanners("All"), locale)}
                {toRender}
            </PropertyBanner>
        );
    } else {
        return null;
    }
};

export default PropertyDetailsPageTemplate;

/**
 * Detect the default property location in belgium
 */
const isDefaultLocation = (location) =>
    location?.latitude === 50.85 && location?.longitude === 4.35;

export const query = graphql`
    query ($id: String!, $locale: String!, $country: String!) {
        agencyFallbackImage: file(relativePath: { eq: "fallback.png" }) {
            __typename
            childImageSharp {
                gatsbyImageData(height: 200, layout: CONSTRAINED)
            }
        }
        property: c21Property(id: { eq: $id }, locale: { eq: $locale }) {
            __typename
            id
            api_id
            datePosted
            location {
                __typename
                latitude
                longitude
            }
            amenities {
                __typename
                cellar
                parking
                swimmingPool
                terrace
            }
            orientationGarden
            destination
            locale
            availability
            yearOfConstruction
            urlVirtualVisit
            numberOfFacades
            hasSubdivisionAuthorization
            hasPreemptionRight
            hasBuildingPermit
            title
            isFloodZone
            condition
            floorNumber
            reference
            hasParking
            description
            type
            subType
            status
            listingType
            price
            availableFrom
            summon
            agency {
                __typename
                api_id
                slug
                name
                phoneNumber
                address {
                    __typename
                    city
                    countryCode
                    postalCode
                    street
                    number
                }
                images {
                    __typename
                    description
                    # fixed(width: 100, height: 100) {
                    #     width
                    #     height
                    #     src
                    #     srcSet
                    #     srcWebp
                    #     srcSetWebp
                    # }
                    gatsbyImageData(width: 500, layout: CONSTRAINED)
                }
            }
            agent {
                __typename
                firstName
                lastName
                images {
                    __typename
                    description
                    # fixed(width: 100, height: 100) {
                    #     width
                    #     height
                    #     src
                    #     srcSet
                    #     srcWebp
                    #     srcSetWebp
                    # }

                    gatsbyImageData(width: 500, layout: FULL_WIDTH)
                }
            }
            energySpecifications {
                __typename
                energyLabel
                energyCertificate
                energyScore {
                    __typename
                    unit
                    value
                }
                energyReportReference
                energySources {
                    __typename
                    name
                }
                totalEnergyConsumption {
                    __typename
                    unit
                    value
                }
                co2Emission {
                    __typename
                    unit
                    value
                }
            }
            surface {
                habitableSurfaceArea {
                    __typename
                    unit
                    value
                }
                surfaceAreaGarden {
                    __typename
                    unit
                    value
                }
                surfaceAreaLivingRoom {
                    __typename
                    unit
                    value
                }
                surfaceAreaKitchen {
                    __typename
                    unit
                    value
                }
                totalSurfaceArea {
                    __typename
                    unit
                    value
                }
            }
            rooms {
                __typename
                numberOfBathrooms
                numberOfBedrooms
            }
            address {
                __typename
                street
                number
                box
                postalCode
                city
                country
                countryCode
                hidden
                region
            }
            metaImage: images {
                __typename
                # meta: fixed(width: 600, height: 314) {
                #     src
                # }

                # TODO uncomment when gatsbyImageData is available
                meta: gatsbyImageData(width: 600, height: 314, layout: FIXED)
            }

            carouselImages: images {
                __typename
                description
                # fixed(width: 670, height: 384) {
                #     width
                #     height
                #     src
                #     srcSet
                #     srcWebp
                #     srcSetWebp
                # }

                # TODO uncomment when gatsbyImageData is available
                gatsbyImageData(width: 670, height: 384, layout: FIXED)
                # fluid(maxWidth: 1134) {
                #     aspectRatio
                #     sizes
                #     src
                #     srcSet
                #     srcWebp
                #     srcSetWebp
                # }

                # TODO uncomment when gatsbyImageData is available
                # fluidImage: gatsbyImageData(width: 1134, quality: 80)
            }
            assets {
                __typename
                name
                href
                description
            }
            project {
                __typename
                slug
                # properties {
                #     __typename
                #     id
                #     type
                #     subType
                #     address {
                #         __typename
                #         street
                #         number
                #         box
                #         postalCode
                #         city
                #         country
                #     }
                #     surface {
                #         __typename
                #         habitableSurfaceArea {
                #             __typename
                #             unit
                #             value
                #         }
                #         surfaceAreaGarden {
                #             __typename
                #             unit
                #             value
                #         }
                #     }
                #     images {
                #         __typename
                #         description
                #         # fixed(width: 310, height: 177) {
                #         #     width
                #         #     height
                #         #     src
                #         #     srcSet
                #         #     srcWebp
                #         #     srcSetWebp
                #         # }
                #         gatsbyImageData(width: 310, height: 177, layout: FIXED)
                #     }
                #     rooms {
                #         __typename
                #         numberOfBathrooms
                #         numberOfBedrooms
                #     }
                #     price
                #     energySpecifications {
                #         __typename
                #         energyLabel
                #         energyScore {
                #             __typename
                #             unit
                #             value
                #         }
                #         energyReportReference
                #         energySources {
                #             __typename
                #             name
                #         }
                #         totalEnergyConsumption {
                #             __typename
                #             unit
                #             value
                #         }
                #         co2Emission {
                #             __typename
                #             unit
                #             value
                #         }
                #     }
                #     slug
                #     numberOfFacades
                #     condition
                #     hasParking
                # }
            }
        }
        carouselFallbackImage: file(relativePath: { eq: "no-image.jpg" }) {
            __typename
            childImageSharp {
                __typename
                fluid: gatsbyImageData(
                    width: 670
                    quality: 100
                    layout: CONSTRAINED
                )
            }
        }
        c21Translation(locale: { eq: $locale }) {
            translations(
                keys: [
                    "property-details.*"
                    "common.text.read-more"
                    "common.text.show-more"
                    "common.text.and"
                    "common.text.yes"
                    "common.text.no"
                    "common.text.unknown"
                    "common.text.maybe"
                    "common.text.language"
                    "common.text.follow-us"
                    "common.price.on-demand"
                    "common.address.on-demand"
                    "common.filters.price.label"
                    "common.year"
                    "common.gas"
                    "common.electricity"
                    "common.heat-pump"
                    "common.fuel"
                    "common.social.*"
                    "common.print"
                    "usp.*"
                    "api.*"
                    "form.agency-contact.*"
                    "slug.privacy-policy"
                    "slug.property-overview"
                    "slug.property-detail"
                    "favourites.*"
                    "title.property-detail *"
                    "breadcrumbs.property*"
                    "agency.header.button.text"
                    "label.estimation-tool"
                    "slug.estimation-tool"
                    "api.property.type.*"
                    "api.property.sub-type.*"
                ]
            )
        }
        contentfulNavigation(
            title: { eq: "Main Navigation" }
            node_locale: { eq: $locale }
            country: { eq: $country }
        ) {
            ...ContentfulNavigation
        }
        topNavigationItems: contentfulNavigation(
            title: { eq: "Main navigation bar" }
            node_locale: { eq: $locale }
            country: { eq: $country }
        ) {
            ...ContentfulNavigation
        }
        socialLinks: allContentfulLink(
            filter: {
                metadata: { tags: { elemMatch: { name: { eq: "Socials" } } } }
                node_locale: { eq: $locale }
            }
        ) {
            nodes {
                linkTitle
                externalLink
            }
        }
        footerItems: contentfulNavigation(
            title: { eq: "Footer" }
            node_locale: { eq: $locale }
            country: { eq: $country }
        ) {
            ...ContentfulNavigation
        }
        contentfulWebLandingPage(
            internalLabel: { eq: "Property detail page" }
            node_locale: { eq: $locale }
        ) {
            pageContent {
                __typename
                ... on ContentfulSearchPageBanner {
                    id
                    typeOfSearch
                    imageLink {
                        __typename
                        ... on ContentfulLink {
                            id
                            linkTitle
                            externalLink
                            contentfulInternalLink {
                                ... on ContentfulWebLandingPage {
                                    slug
                                }
                            }
                        }
                    }
                    image {
                        __typename
                        # fluid(maxWidth: 2344, quality: 80) {
                        #     ...GatsbyImageSharpFluid
                        # }
                        gatsbyImageData(width: 2344, quality: 80)
                        title
                        description
                    }
                    country
                }
            }
        }
    }
`;
